import { message } from 'antd'

/**
 * Extracts error message from API response and displays it with context
 * Handles multiple possible error structures from different API clients
 */
export function extractAndDisplayError(
  error: unknown,
  fallbackMessage = 'An error occurred. Please try again.'
): void {
  let apiErrorDetail = ''

  // Extract the specific error detail from API response
  if (error && typeof error === 'object') {
    // Try different possible error structures
    const anyError = error as any

    // OpenAPI React Query error structure
    if (anyError.error?.detail) {
      apiErrorDetail = anyError.error.detail
    }
    // Standard axios/fetch error structure
    else if (anyError.response?.data?.detail) {
      apiErrorDetail = anyError.response.data.detail
    }
    // Direct detail property
    else if (anyError.detail) {
      apiErrorDetail = anyError.detail
    }
    // Message property
    else if (anyError.message) {
      apiErrorDetail = anyError.message
    }
    // For debugging - log the error structure in development
    if (process.env.NODE_ENV === 'development') {
      console.log('Error structure:', JSON.stringify(error, null, 2))
    }
  } else if (error instanceof Error) {
    apiErrorDetail = error.message
  }

  // Combine context with API error details for better user experience
  let finalMessage = fallbackMessage
  if (apiErrorDetail && apiErrorDetail.trim() && apiErrorDetail !== 'An error occurred. Please try again.') {
    // If we have specific API error details, combine with context
    if (fallbackMessage.includes(':')) {
      // Fallback already has format "Context: details", replace details part
      finalMessage = fallbackMessage.split(':')[0] + ': ' + apiErrorDetail
    } else {
      // Add API details to the fallback message
      finalMessage = `${fallbackMessage.replace(/\. Please try again\.$/, '')}: ${apiErrorDetail}`
    }
  }

  message.error(finalMessage)
}

/**
 * Extracts error message from API response without displaying it
 * Useful when you need the message string for custom handling
 */
export function extractErrorMessage(
  error: unknown,
  fallbackMessage = 'An error occurred. Please try again.'
): string {
  let apiErrorDetail = ''

  // Extract the specific error detail from API response
  if (error && typeof error === 'object') {
    // Try different possible error structures
    const anyError = error as any

    // OpenAPI React Query error structure
    if (anyError.error?.detail) {
      apiErrorDetail = anyError.error.detail
    }
    // Standard axios/fetch error structure
    else if (anyError.response?.data?.detail) {
      apiErrorDetail = anyError.response.data.detail
    }
    // Direct detail property
    else if (anyError.detail) {
      apiErrorDetail = anyError.detail
    }
    // Message property
    else if (anyError.message) {
      apiErrorDetail = anyError.message
    }
  } else if (error instanceof Error) {
    apiErrorDetail = error.message
  }

  // Combine context with API error details for better user experience
  let finalMessage = fallbackMessage
  if (apiErrorDetail && apiErrorDetail.trim() && apiErrorDetail !== 'An error occurred. Please try again.') {
    // If we have specific API error details, combine with context
    if (fallbackMessage.includes(':')) {
      // Fallback already has format "Context: details", replace details part
      finalMessage = fallbackMessage.split(':')[0] + ': ' + apiErrorDetail
    } else {
      // Add API details to the fallback message
      finalMessage = `${fallbackMessage.replace(/\. Please try again\.$/, '')}: ${apiErrorDetail}`
    }
  }

  return finalMessage
}
